import { ICheckoutDto } from "@kuika/kuika-cl-model";
import { Location, NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import LocalStorageService from "../../auth/local-storage-service";
import { PaymentService } from "../../services/payment-service";
import { UserService } from "../../services/user-service";

declare let window: Window & { currentDomain: string; skipAlert: boolean };

export interface IRouterLocation {
  pathname: string;
  state?: any;
}

interface ILegacyHistory {
  push: (path: string | { pathname: string; state?: unknown }) => void;
  replace: (path: string | { pathname: string; state?: unknown }) => void;
  location?: { pathname: string; state?: unknown };
}

export interface IWithHistoryProp {
  navigator: KNavigator;
  history?: ILegacyHistory;
  location?: ReturnType<typeof useLocation>;
}

export const withHistory = <P extends object>(Component: any) => {
  return (props: P) => {
    const navigate = useNavigate();
    const location = useLocation();
    return (
      <Component {...props} navigator={new KNavigator(navigate, location)} location={location} history={navigate} />
    );
  };
};

export class KNavigator {
  constructor(
    public navigate: NavigateFunction,
    public location: Location
  ) {}

  public gotoLogin = () => {
    this.navigate({
      pathname: "/login"
    });
  };

  public goToChangePassword = (username?: string) => {
    this.navigate({
      pathname: "/changepassword",
      search: username ? `?username=${username}` : ""
    });
  };

  public goToResetPassword = (user: string, token: string) => {
    this.navigate({
      pathname: "/resetpassword",
      search: `?user=${user}&token=${token}`
    });
  };

  public goToEmailConfirmation = () => {
    this.navigate({ pathname: "/email-confirmation" });
  };

  public gotoExcelImport = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/component/excelimport`
    });
  };

  public gotoRoot = () => {
    this.navigate({
      pathname: "/"
    });
  };

  public gotoUnsupportedDevice = () => {
    this.navigate({
      pathname: "/unsupported-device"
    });
  };

  public gotoNewComponent = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/component/newcomponent`
    });
  };

  public gotoScreen = (id: string) => {
    this.navigate(`/${window.currentDomain}/sd/${id}`);
  };

  public goto = (id: string) => {
    this.navigate(`/sd/${id}`);
  };

  public gotoSd = (id: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/sd/${id}`
    });
  };

  public goto2 = (type) => {
    this.navigate(type);
  };

  public gotoUser = (id: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/user/${id}`,
      search: `?id=${id}`
    });
  };

  public gotoForgotPassword = () => {
    this.navigate("/forgotpassword");
  };

  public goToPasswordChangeSuccess = () => {
    this.navigate("/passwordchangesuccess");
  };

  public gotoUrl = (url: string) => {
    this.navigate(url);
  };

  public gotoRole = (id: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/role/${id}`,
      search: `?id=${id}`
    });
  };

  public gotoEditComponent = (id: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/component/editcomponent`,
      search: `?id=${id}`
    });
  };

  public gotoLogs = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/logs/`
    });
  };

  public gotoUsers = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/users/`
    });
  };

  public gotoRoles = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/roles/`
    });
  };

  public gotoComponents = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/components/`
    });
  };

  public gotoUiKits = (key?: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/ui-kit/${key || ""}`
    });
  };

  public goToExactDomain = (domainName: string, showAllProjects?: boolean) => {
    LocalStorageService.setCurrentDomain(domainName);
    this.navigate({
      pathname: `/${domainName}/projects/${showAllProjects ? "allprojects" : ""}`
    });
  };

  public gotoProjects = () => {
    if (window.currentDomain) {
      this.navigate({
        pathname: `/${window.currentDomain}/projects`
      });
    } else {
      this.navigate({
        pathname: "/"
      });
    }
  };

  public gotoSettings = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings`
    });
  };

  public gotoDashboard = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/dashboard`
    });
  };

  public gotoAllProjects = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/allprojects`
    });
  };

  public gotoTemplates = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/templates`
    });
  };

  public gotoRecent = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/recent`
    });
  };

  public gotoMyProjects = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/myprojects`
    });
  };

  public gotoFavorites = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/favorites`
    });
  };

  public gotoArchive = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/projects/archive`
    });
  };

  public gotoPreferences = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/preferences`
    });
  };

  public gotoComponentSet = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/componentset`
    });
  };

  public gotoNewUsers = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/users`
    });
  };

  public gotoBilling = () => {
    if (window.location.host.toString().includes("platform.kuika.com")) {
      window.open("https://billing.stripe.com/p/login/14k3f70wPeT60GAcMM", "_blank");
    } else {
      window.open("https://billing.stripe.com/p/login/test_00g9DgccL7ZAgQ8144", "_blank");
    }
  };

  public gotoNewRoles = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/roles`
    });
  };

  public gotoSubsciptions = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/subscriptionPlans`
    });
  };

  public gotoNewLogs = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/logs`
    });
  };

  public goBack = () => {
    this.navigate(-1);
  };

  public gotoA = () => {
    // this.history
  };

  public gotoComponentList = (componentSetId: string = "") => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/componentlist/${componentSetId}`
    });
  };

  public getCurrentDomain = () => {
    return LocalStorageService.getCurrentDomain();
  };

  public setCurrentDomain = (domainName: string) => {
    LocalStorageService.setCurrentDomain(domainName);
    this.gotoProjects();
    window.location.reload();
  };

  public gotoSubscriptionPlans = (id: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/subscriptionPlans`
    });
  };

  public goToSuccessCheckout = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/subscriptionPlans/successSubscriptionCheckout`
    });
  };

  public goToFailedCheckout = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/subscriptionPlans/failedSubscriptionCheckout`
    });
  };

  public goToUpgradeSubscription = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/subscriptionPlans/upgradedSubscription`
    });
  };

  public gotoChangePlan = async (
    id: string,
    successUrl: string,
    failUrl: string,
    changePlan: boolean,
    nextId: string | undefined
  ) => {
    const obj = {} as ICheckoutDto;
    obj.FailUrl = failUrl;
    obj.SuccessUrl = successUrl;
    obj.PriceId = nextId || id;
    if (!id) {
      const res = (await PaymentService.CancelSubscription()).data;
      window.skipAlert = true;
      window.location.replace(failUrl);
    } else if (changePlan) {
      window.skipAlert = true;
      const res = (await PaymentService.UpdateSubscription(obj)).data;
      window.location.replace(failUrl);
    } else {
      window.skipAlert = true;
      const res = (await PaymentService.Checkout(obj)).data;
      window.location.replace(res);
    }
    await UserService.GetUserInfo();
  };

  public goToGlobalConfig = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/globalConfigurations`
    });
  };

  public gotoBoard = (boardCode?: string, issueKey?: string) => {
    const base: string = `/${window.currentDomain}/board`;
    let pathname: string = base;
    if (boardCode) {
      pathname = `${base}/${boardCode}`;
      if (issueKey) pathname = `${pathname}/${issueKey}`;
    }
    this.navigate({ pathname });
  };

  public gotoIssue = (issueKey: string) => {
    this.navigate({
      pathname: `/${window.currentDomain}/issue/${issueKey}`
    });
  };

  public gotoCloud = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/cloud/domains`
    });
  };

  public gotoCloudDomains = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/cloud/domains`
    });
  };

  public gotoCloudDeployments = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/cloud/deployments`
    });
  };

  public gotoOnboarding = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/onboarding`
    });
  };

  public gotoAiChatbotCredits = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/ai-chatbot-credits`
    });
  };

  public gotoAiChatbotAdmin = () => {
    this.navigate({
      pathname: `/${window.currentDomain}/settings/ai-chatbot-admin`
    });
  };
}
